import { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { IRootState } from "../../../../../modules";
import {
  hasAwsCloudContracts,
  hasAzureCloudContracts,
  hasGCPCloudContracts,
  hasOffice365CloudContracts
} from "../../../../../functions/contractHelper";
import { hasIntegrationsByType } from "../../../../../functions/getCloudReportInformation";
import GlobalConstants from "@constants";
import { useClaims } from "../../../../../auth/ClaimsProvider";
import {
  getIntegrationsCollection,
  selectVisibleAzureCloudReports
} from "../../../../../modules/rtk/integrationsCollector/integrationsCollectorSlice";
import { getMyContracts } from "../../../../../modules/rtk/contract/contractSlice";
import _ from "lodash";

function useGetDataDogCloud() {
  const dispatch = useDispatch();

  const integrationsCollection = useSelector(
    (state: IRootState) => state.integrationsCollection,
    shallowEqual,
  );

  const integrationsCollectorFetchStatus =
    integrationsCollection.integrationsCollectorFetchStatus;

  useEffect(() => {
    if (integrationsCollectorFetchStatus === "idle") {
      // @ts-ignore
      dispatch(getIntegrationsCollection());
    }
  }, [integrationsCollectorFetchStatus]);

  const hasDatadogUsageReports = hasIntegrationsByType(
    integrationsCollection,
    GlobalConstants.integrationType.datadogUsageReport,
  );

  return {
    hasDatadogUsageReports,
  };
}

function useGetGcpCloud() {
  const dispatch = useDispatch();

  const integrationsCollection = useSelector(
    (state: IRootState) => state.integrationsCollection,
    shallowEqual,
  );

  const integrationsCollectorFetchStatus =
    integrationsCollection.integrationsCollectorFetchStatus;

  useEffect(() => {
    if (integrationsCollectorFetchStatus === "idle") {
      dispatch(getIntegrationsCollection());
    }
  }, [integrationsCollectorFetchStatus]);

  const hasGcpUsageReportIntegrations = hasIntegrationsByType(
    integrationsCollection,
    GlobalConstants.integrationType.gcpUsageReport,
  );
  return { hasGcpUsageReportIntegrations };
}

function useGetAwsCloud() {
  const dispatch = useDispatch();

  const integrationsCollection = useSelector(
    (state: IRootState) => state.integrationsCollection,
    shallowEqual,
  );

  const integrationsCollectorFetchStatus =
    integrationsCollection.integrationsCollectorFetchStatus;

  useEffect(() => {
    if (integrationsCollectorFetchStatus === "idle") {
      dispatch(getIntegrationsCollection());
    }
  }, [integrationsCollectorFetchStatus]);

  const awsBillingReport = hasIntegrationsByType(
    integrationsCollection,
    GlobalConstants.integrationType.awsBillingReport,
  );

  return {
    awsBillingReport,
  };
}

function useGetCloudContracts() {
  const [hasAwsMasterPayerAccounts, setHasAwsMasterPayerAccounts] =
    useState(false);
  const dispatch = useDispatch();

  const { cloudContractsFetchStatus, cloudContracts } = useSelector(
    (state: IRootState) => state.contract,
  );

  useEffect(() => {
    if (cloudContractsFetchStatus === GlobalConstants.fetchStatus.idle) {
      dispatch(getMyContracts());
    }
  }, [cloudContractsFetchStatus]);

  const teamContracts = useSelector(
    (state: IRootState) => state.team.teamContracts,
  );

  const hasAzureContracts = hasAzureCloudContracts(cloudContracts);
  const hasAwsContracts = hasAwsCloudContracts(cloudContracts);
  const hasOffice365Contracts = hasOffice365CloudContracts(cloudContracts);
  const hasGcpContracts = hasGCPCloudContracts(teamContracts, cloudContracts);

  useEffect(() => {
    if (cloudContracts && cloudContracts.length > 0) {
      cloudContracts.forEach((cc) => {
        if (
          cc.cloud === "AWS" &&
          cc.userDefinedFieldsJson &&
          cc.userDefinedFieldsJson !== "[]"
        ) {
          const awsPayerID = _.find(
            JSON.parse(cc.userDefinedFieldsJson),
            (udf) => {
              return udf.Name === "AWS Payer ID";
            },
          );
          if (awsPayerID && awsPayerID.Value) {
            setHasAwsMasterPayerAccounts(true);
          }
        }
      });
    }
  }, [cloudContracts]);

  return {
    hasAwsMasterPayerAccounts,
    hasAzureContracts,
    hasAwsContracts,
    hasOffice365Contracts,
    hasGcpContracts,
  };
}

function useGetAzureCloud() {
  const dispatch = useDispatch();

  const integrationsCollection = useSelector((state
                                              : IRootState) => state.integrationsCollection, shallowEqual,);

  const integrationsCollectorFetchStatus =
    integrationsCollection.integrationsCollectorFetchStatus;

  useEffect(() => {
    if (integrationsCollectorFetchStatus === "idle") {
      // @ts-ignore
      dispatch(getIntegrationsCollection());
    }
  }, [integrationsCollectorFetchStatus, dispatch]);

  const hasAzureBackupIntegrations = hasIntegrationsByType(
    integrationsCollection,
    GlobalConstants.integrationType.azureBackups,
  );
  const hasAzureGovernanceReportsIntegration = hasIntegrationsByType(
    integrationsCollection,
    GlobalConstants.integrationType.azureGovernanceReports,
  );
  const hasAzureComplianceIntegrations = hasIntegrationsByType(
    integrationsCollection,
    GlobalConstants.integrationType.azureCompliance,
  );

  const hasAzureRecommendations = hasIntegrationsByType(
    integrationsCollection,
    GlobalConstants.integrationType.azureSimpleRecommendations,
  );

  return {
    hasAzureGovernanceReportsIntegration,
    hasAzureComplianceIntegrations,
    hasAzureBackupIntegrations,
    hasAzureRecommendations,
  };
}

export default function useGetCloudNavPermission() {
  const hasAzureCloudReports = useSelector(selectVisibleAzureCloudReports);
  const { getClaimValueAsNumber } = useClaims();
  const accountId = getClaimValueAsNumber("accountId");
  const contosoDemo =
    accountId === GlobalConstants.demoAccounts.contoso;

  const {
    hasAzureContracts,
    hasAwsContracts,
    hasOffice365Contracts,
    hasGcpContracts,
    hasAwsMasterPayerAccounts,
  } = useGetCloudContracts();
  const {
    hasAzureBackupIntegrations,
    hasAzureComplianceIntegrations,
    hasAzureGovernanceReportsIntegration,
    hasAzureRecommendations,
  } = useGetAzureCloud();
  const { awsBillingReport } = useGetAwsCloud();
  const { hasDatadogUsageReports } = useGetDataDogCloud();
  const { hasGcpUsageReportIntegrations } = useGetGcpCloud();

  const hasCloudAccounts =
    hasAzureContracts ||
    hasAwsContracts ||
    hasOffice365Contracts ||
    hasGcpContracts;

  const hasAzureIntegrations =
    hasAzureRecommendations ||
    hasAzureComplianceIntegrations ||
    hasAzureBackupIntegrations;

  const hasAzureReports =
    hasAzureCloudReports &&
    (hasAzureCloudReports.hasAzurePlanReportIntegration ||
      hasAzureCloudReports.hasAzureDirectConsumptionIntegration ||
      hasAzureCloudReports.hasAzureDevOpsReportIntegration);

  const showAzureNav =
    hasAzureReports || hasAzureContracts || hasAzureIntegrations;

  const showAwsNav = awsBillingReport || hasAwsContracts;
  const showGcpNav = hasGcpContracts || hasGcpUsageReportIntegrations;

  const showOffice365Nav = hasOffice365Contracts;

  const showCloudNav =
    showAzureNav ||
    showAwsNav ||
    hasAzureContracts ||
    hasAwsContracts ||
    showOffice365Nav ||
    hasAzureIntegrations ||
    showGcpNav ||
    hasGcpContracts ||
    hasDatadogUsageReports;

  return {
    showCloudNav,
    hasCloudAccounts,
    hasAwsMasterPayerAccounts,
    contosoDemo,
    showAzureNav,
    showAwsNav,
    showOffice365Nav,
    showGcpNav,
    hasAzureContracts,
    hasAwsContracts,
    hasOffice365Contracts,
    hasGcpContracts,
    hasAzureBackupIntegrations,
    hasAzureComplianceIntegrations,
    hasAzureGovernanceReportsIntegration,
    hasAzureRecommendations,
    awsBillingReport,
    hasGcpUsageReportIntegrations,
    hasDatadogUsageReports,
  };
}
